// i18n — global translation system with reactive UI updates.

import { state, subscribe } from './state.js';
import { fb } from './firebase.js';
import { metaRef } from './paths.js';

const STRINGS = {
  es: {
    // Common
    welcome: 'Bienvenido',
    login: 'Iniciar sesión',
    signup: 'Crear cuenta',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Guardar',
    delete: 'Eliminar',
    edit: 'Editar',
    add: 'Agregar',
    close: 'Cerrar',
    back: 'Volver',
    continue: 'Continuar',
    finish: 'Finalizar',
    next: 'Siguiente',
    yes: 'Sí',
    no: 'No',
    loading: 'Cargando…',
    error_generic: 'Algo salió mal. Intenta de nuevo.',
    error_required: 'Campo requerido',
    error_offline: 'Sin conexión',
    sync_online: 'ONLINE',
    sync_syncing: 'SYNC…',
    sync_offline: 'OFFLINE',

    // Auth
    auth_title: 'BIENVENIDO',
    auth_subtitle: 'Crea o entra a tu cuenta familiar',
    auth_tagline: 'Convierte la vida en un juego',
    auth_btn_login: 'ENTRAR',
    auth_btn_signup: 'REGISTRAR',
    auth_btn_start: 'COMENZAR AVENTURA!',
    auth_btn_create: 'CREAR CUENTA',
    auth_processing: '⏳ Procesando…',
    family_name: 'Nombre de la familia',
    family_name_placeholder: 'Ej: Los Increíbles',
    email: 'Email',
    email_placeholder: 'papa@ejemplo.com',
    password: 'Contraseña',
    invite_link: '¿Te invitaron? Usa tu código',
    invite_code: 'Código de invitación',
    invite_code_placeholder: 'ABC123',

    // Hero-select
    who_plays: '¿QUIÉN JUEGA HOY?',
    role_master: 'MAESTRO',
    role_master_desc: 'Panel de papá — gestionar misiones',
    no_heroes: 'No hay héroes creados.\nEntra como Maestro para añadir uno.',

    // Hero dashboard
    hero_label: 'HÉROE',
    this_week: 'ESTA SEMANA',
    today_missions: '⚔️ Misiones de hoy',
    history: '📜 Historial',
    no_missions: '🎯 No hay misiones para hoy',
    no_history: 'Sin actividad aún',
    discover_archetype: '🎭 Descubre tu arquetipo',

    // Stats
    stat_lives: 'VIDAS',
    stat_xp: 'XP',
    stat_coins: 'COINS',
    stat_streak: 'RACHA',
    level: 'NIVEL',

    // Pulse
    pulse_question: '¿Cómo te sientes hoy?',
    pulse_reward: '+5 🪙 por contarnos',
    pulse_thanks: '❤️ ¡Gracias! +5 🪙',

    // Shop
    shop_title: 'TIENDA',
    shop_available: '🛒 Premios disponibles',
    shop_no_rewards: 'Sin premios aún',
    shop_my_requests: '📋 Mis solicitudes',
    shop_propose_title: '💡 Propón un premio',
    shop_reward_name: 'Nombre del premio',
    shop_reward_name_ph: 'Ej: Película familiar',
    shop_reward_cost: 'Costo en coins',
    shop_reward_desc: 'Descripción (opcional)',
    shop_reward_desc_ph: '¿Por qué te gustaría este premio?',
    shop_propose_send: '📤 Enviar propuesta',
    shop_redeem: 'Canjear',
    shop_redeem_confirm: '¿Canjear "{name}" por 🪙{cost}?',
    shop_insufficient: 'No tienes suficientes coins',
    shop_done: '✨ Canje realizado',
    shop_pending: '📨 Pedido enviado al maestro',
    shop_proposal_sent: '💡 Propuesta enviada',
    shop_propose_no_name: 'Escribe un nombre',
    shop_propose_invalid_cost: 'Costo mínimo 5',

    // Calendar
    calendar_title: 'CALENDARIO',
    calendar_upcoming: '📌 Próximos eventos',
    calendar_no_events: 'Sin eventos próximos',
    calendar_hint_create: '✨ Toca un día para crear un evento',
    calendar_hint_view: '✨ Toca un día para ver sus eventos',
    calendar_new_event: '➕ Crear nuevo evento',
    calendar_event_title: 'Título del evento',
    calendar_event_time: 'Hora (opcional)',
    calendar_event_desc: 'Descripción (opcional)',
    calendar_save_event: 'GUARDAR EVENTO',
    calendar_event_created: 'Evento creado 🎉',
    calendar_no_day_events: 'Sin eventos este día',
    calendar_delete_confirm: '¿Eliminar evento?',
    calendar_event_deleted: 'Eliminado',

    // Reports
    reports_title: 'REPORTES',
    reports_select_hero: 'Selecciona un héroe para ver su reporte',
    reports_choose_hero: 'Selecciona héroe…',
    reports_perfect: 'Días perfectos',
    reports_partial: 'Parciales',
    reports_failed: 'Fallidos',
    reports_xp: 'XP ganado',
    reports_earned: 'Ganados',
    reports_spent: 'Gastados',
    reports_avg_pulse: 'Pulso promedio',
    reports_month_cal: '📅 Calendario del mes',
    reports_top_5: '🏆 Top 5 misiones',
    reports_no_activity: 'Sin actividad este mes',
    reports_redemptions: '🎁 Canjes del mes',
    reports_no_redemptions: 'Sin canjes',
    reports_day_completed: 'Completadas',
    reports_day_pending: 'Pendientes',
    reports_day_missed: 'No hechas',
    reports_day_pulse: '💓 PULSO EMOCIONAL',
    reports_day_no_pulse: 'Sin registro de pulso este día',
    reports_day_missions: '⚔️ Misiones del día',
    reports_day_activity: '📜 Actividad registrada',

    // Admin
    admin_title: 'PANEL MAESTRO',
    admin_pending: 'Pendientes',
    admin_missions: 'Misiones',
    admin_shop: 'Tienda',
    admin_heroes: 'Héroes',
    admin_alerts: 'Alertas',
    admin_pulse: 'Pulso',
    admin_stats: 'Stats',
    admin_family: 'Familia',
    admin_packs: 'Packs',
    nothing_pending: '✨ Nada pendiente',

    // Menu
    menu_title: '⚙️ MENÚ',
    menu_change_theme: '🎨  Cambiar tema',
    menu_view_pro: '⭐  Ver HeroMind Pro',
    menu_switch_role: '🔄  Cambiar de rol',
    menu_switch_hero: '🔄  Cambiar de héroe',
    menu_logout: '🚪  Cerrar sesión',
    logout_confirm: '¿Cerrar sesión?',
    logout_title: 'Salir',

    // Wellness
    wellness_title: 'BIENESTAR',
    wellness_intro: 'Detectamos señales cruzadas (emociones, hábitos, comportamiento) para alertarte sobre el bienestar de tus héroes. Información orientativa, no diagnóstico.',
    wellness_all_good: 'Todo en orden',
    wellness_no_alerts: 'Sin alertas activas. Tus héroes están bien.',
    wellness_helplines: '🆘 LÍNEAS DE AYUDA',
    wellness_signals: 'SEÑALES DETECTADAS',
    wellness_recommend: '🧠 Ver psicólogos sugeridos',
    wellness_mark_seen: 'Marcar vista',

    // Catalog
    catalog_title: 'PSICÓLOGOS',
    catalog_disclaimer: 'HeroMind no presta servicios de salud mental. Sólo facilitamos el contacto con profesionales verificados. La relación es directa entre tu familia y el psicólogo.',
    catalog_no_results: 'No encontramos psicólogos con esos filtros aún.',
    catalog_specialty: 'Especialidad',

    // Subscriptions
    pro_title: 'PRO',
    pro_unlock: 'Desbloquea HeroMind Pro',
    pro_subtitle: 'Coach IA · Análisis emocional · Y mucho más',
    pro_subscribe: 'Suscribirme',
    pro_manage: 'Gestionar suscripción existente →',
    pro_cancel_note: 'Cancela cuando quieras desde esta pantalla.',
    pro_not_active: 'No tienes suscripción activa',
    pro_not_configured: 'Los pagos aún no están configurados. Próximamente.',

    // Coach IA
    coach_title: 'COACH IA',
    coach_desc: 'Misiones con IA',
    coach_intro: 'Genera misiones personalizadas según la edad, intereses y dificultades de tu héroe.',
    coach_target_hero: 'Héroe objetivo',
    coach_all_heroes: 'Todos los héroes (genérico)',
    coach_context_label: 'Contexto (opcional)',
    coach_context_ph: 'Intereses, dificultades, qué quieres reforzar…',
    coach_generate: '⚡ Generar 5 misiones',
    coach_generated: '{n} misiones generadas',
    coach_no_results: 'Genera misiones para verlas aquí',
    coach_accept: '✓ AÑADIR',
    coach_pro_lock: 'Coach IA',
    coach_pro_cta: '✨ Desbloquear con Pro',
  },
  en: {
    welcome: 'Welcome',
    login: 'Sign in',
    signup: 'Sign up',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    close: 'Close',
    back: 'Back',
    continue: 'Continue',
    finish: 'Finish',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading…',
    error_generic: 'Something went wrong. Try again.',
    error_required: 'Required field',
    error_offline: 'No connection',
    sync_online: 'ONLINE',
    sync_syncing: 'SYNC…',
    sync_offline: 'OFFLINE',

    auth_title: 'WELCOME',
    auth_subtitle: 'Create or sign in to your family account',
    auth_tagline: 'Turn life into a game',
    auth_btn_login: 'SIGN IN',
    auth_btn_signup: 'SIGN UP',
    auth_btn_start: 'START ADVENTURE!',
    auth_btn_create: 'CREATE ACCOUNT',
    auth_processing: '⏳ Processing…',
    family_name: 'Family name',
    family_name_placeholder: 'E.g. The Incredibles',
    email: 'Email',
    email_placeholder: 'dad@example.com',
    password: 'Password',
    invite_link: 'Got invited? Use your code',
    invite_code: 'Invitation code',
    invite_code_placeholder: 'ABC123',

    who_plays: 'WHO PLAYS TODAY?',
    role_master: 'MASTER',
    role_master_desc: "Parent panel — manage missions",
    no_heroes: "No heroes yet.\nLog in as Master to add one.",

    hero_label: 'HERO',
    this_week: 'THIS WEEK',
    today_missions: "⚔️ Today's missions",
    history: '📜 History',
    no_missions: '🎯 No missions for today',
    no_history: 'No activity yet',
    discover_archetype: '🎭 Discover your archetype',

    stat_lives: 'LIVES',
    stat_xp: 'XP',
    stat_coins: 'COINS',
    stat_streak: 'STREAK',
    level: 'LEVEL',

    pulse_question: 'How are you feeling today?',
    pulse_reward: '+5 🪙 for telling us',
    pulse_thanks: '❤️ Thanks! +5 🪙',

    shop_title: 'SHOP',
    shop_available: '🛒 Available rewards',
    shop_no_rewards: 'No rewards yet',
    shop_my_requests: '📋 My requests',
    shop_propose_title: '💡 Propose a reward',
    shop_reward_name: 'Reward name',
    shop_reward_name_ph: 'E.g. Family movie night',
    shop_reward_cost: 'Cost in coins',
    shop_reward_desc: 'Description (optional)',
    shop_reward_desc_ph: 'Why would you like this reward?',
    shop_propose_send: '📤 Send proposal',
    shop_redeem: 'Redeem',
    shop_redeem_confirm: 'Redeem "{name}" for 🪙{cost}?',
    shop_insufficient: 'Not enough coins',
    shop_done: '✨ Redeemed',
    shop_pending: '📨 Request sent to master',
    shop_proposal_sent: '💡 Proposal sent',
    shop_propose_no_name: 'Type a name',
    shop_propose_invalid_cost: 'Minimum cost is 5',

    calendar_title: 'CALENDAR',
    calendar_upcoming: '📌 Upcoming events',
    calendar_no_events: 'No upcoming events',
    calendar_hint_create: '✨ Tap a day to create an event',
    calendar_hint_view: '✨ Tap a day to see its events',
    calendar_new_event: '➕ Create new event',
    calendar_event_title: 'Event title',
    calendar_event_time: 'Time (optional)',
    calendar_event_desc: 'Description (optional)',
    calendar_save_event: 'SAVE EVENT',
    calendar_event_created: 'Event created 🎉',
    calendar_no_day_events: 'No events this day',
    calendar_delete_confirm: 'Delete event?',
    calendar_event_deleted: 'Deleted',

    reports_title: 'REPORTS',
    reports_select_hero: 'Select a hero to view report',
    reports_choose_hero: 'Choose hero…',
    reports_perfect: 'Perfect days',
    reports_partial: 'Partial',
    reports_failed: 'Failed',
    reports_xp: 'XP earned',
    reports_earned: 'Earned',
    reports_spent: 'Spent',
    reports_avg_pulse: 'Avg pulse',
    reports_month_cal: '📅 Month calendar',
    reports_top_5: '🏆 Top 5 missions',
    reports_no_activity: 'No activity this month',
    reports_redemptions: '🎁 Redemptions this month',
    reports_no_redemptions: 'No redemptions',
    reports_day_completed: 'Done',
    reports_day_pending: 'Pending',
    reports_day_missed: 'Missed',
    reports_day_pulse: '💓 EMOTIONAL PULSE',
    reports_day_no_pulse: 'No pulse recorded this day',
    reports_day_missions: '⚔️ Day missions',
    reports_day_activity: '📜 Activity log',

    admin_title: 'MASTER PANEL',
    admin_pending: 'Pending',
    admin_missions: 'Missions',
    admin_shop: 'Shop',
    admin_heroes: 'Heroes',
    admin_alerts: 'Alerts',
    admin_pulse: 'Pulse',
    admin_stats: 'Stats',
    admin_family: 'Family',
    admin_packs: 'Packs',
    nothing_pending: '✨ Nothing pending',

    menu_title: '⚙️ MENU',
    menu_change_theme: '🎨  Change theme',
    menu_view_pro: '⭐  View HeroMind Pro',
    menu_switch_role: '🔄  Switch role',
    menu_switch_hero: '🔄  Switch hero',
    menu_logout: '🚪  Sign out',
    logout_confirm: 'Sign out?',
    logout_title: 'Exit',

    wellness_title: 'WELLBEING',
    wellness_intro: 'We detect crossed signals (emotions, habits, behavior) to alert you about your heroes wellbeing. Orientative info, not a diagnosis.',
    wellness_all_good: 'All good',
    wellness_no_alerts: 'No active alerts. Your heroes are fine.',
    wellness_helplines: '🆘 HELPLINES',
    wellness_signals: 'DETECTED SIGNALS',
    wellness_recommend: '🧠 Suggested psychologists',
    wellness_mark_seen: 'Mark as seen',

    catalog_title: 'PSYCHOLOGISTS',
    catalog_disclaimer: "HeroMind doesn't provide mental health services. We only facilitate contact with verified professionals. The relationship is direct between your family and the psychologist.",
    catalog_no_results: "No psychologists match those filters yet.",
    catalog_specialty: 'Specialty',

    pro_title: 'PRO',
    pro_unlock: 'Unlock HeroMind Pro',
    pro_subtitle: 'AI Coach · Emotional analysis · Much more',
    pro_subscribe: 'Subscribe',
    pro_manage: 'Manage existing subscription →',
    pro_cancel_note: 'Cancel anytime from this screen.',
    pro_not_active: 'No active subscription',
    pro_not_configured: 'Payments not configured yet. Coming soon.',

    coach_title: 'AI COACH',
    coach_desc: 'AI Missions',
    coach_intro: 'Generate personalized missions based on your hero\'s age, interests and difficulties.',
    coach_target_hero: 'Target hero',
    coach_all_heroes: 'All heroes (generic)',
    coach_context_label: 'Context (optional)',
    coach_context_ph: 'Interests, difficulties, what to reinforce…',
    coach_generate: '⚡ Generate 5 missions',
    coach_generated: '{n} missions generated',
    coach_no_results: 'Generate missions to see them here',
    coach_accept: '✓ ADD',
    coach_pro_lock: 'AI Coach',
    coach_pro_cta: '✨ Unlock with Pro',
  },
};

const LANG_KEY = 'heromind.lang';

export function getLang() {
  return state.meta?.lang || localStorage.getItem(LANG_KEY) || 'es';
}

export function t(key, vars = {}) {
  const lang = getLang();
  let str = STRINGS[lang]?.[key] || STRINGS.es[key] || key;
  for (const [k, v] of Object.entries(vars)) str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v);
  return str;
}

export async function setLang(lang) {
  if (!STRINGS[lang]) return;
  localStorage.setItem(LANG_KEY, lang);
  state.meta = { ...state.meta, lang };
  if (state.familyId) {
    try { await fb.updateDoc(metaRef(), { lang }); } catch {}
  }
  applyLang();
  // Trigger re-render of mounted screen
  triggerScreenRefresh();
}

function triggerScreenRefresh() {
  const cur = state.currentScreen;
  // Force navigate to same route to force a re-mount
  state.currentScreen = '__force__';
  setTimeout(() => {
    import('./router.js').then(r => r.navigate(cur));
  }, 50);
}

export function applyLang() {
  document.documentElement.lang = getLang();
  document.querySelectorAll('[data-i18n]').forEach(el => {
    const key = el.getAttribute('data-i18n');
    el.textContent = t(key);
  });
  document.querySelectorAll('[data-i18n-placeholder]').forEach(el => {
    const key = el.getAttribute('data-i18n-placeholder');
    el.setAttribute('placeholder', t(key));
  });
}

// Re-apply when meta loads / changes
subscribe('meta', () => applyLang());

if (typeof window !== 'undefined') {
  window.t = t;
  window.setLang = setLang;
  window.getLang = getLang;
}
